/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { DatadogRumConfig } from '@/types/config';

/**
 * Datadog browser agent — US1 region, major version 6.
 *
 * SFRA pins `v5` (`app_core_org/.../datadog_sfcc_script.isml`). Starting a brand-new integration a
 * major version behind buys nothing, and every option this file sets exists identically in both, so
 * Black Magic starts on the current major. Bumping SFRA later — or pinning back to v5 for strict
 * parity — is a change to this one string.
 */
export const DATADOG_RUM_AGENT_SRC = 'https://www.datadoghq-browser-agent.com/us1/v6/datadog-rum.js';

/**
 * The `<head>` loader stub, verbatim from Datadog's async CDN snippet.
 *
 * Installs `window.DD_RUM` as a queue (`{ q: [], onReady(cb) }`) and appends the agent `<script>`.
 * When the agent lands it replaces the global with the real API and drains `q`, so every call in
 * this module can go through `onReady` and be safe whether or not the agent has arrived — the same
 * queue-then-replay contract as `window.adobeDataLayer` and the OneTrust `OptanonWrapper` shim.
 *
 * Rendered as a nonce'd inline script by `root.tsx`. The agent script it appends carries **no**
 * nonce (a runtime-injected script can't), so `www.datadoghq-browser-agent.com` is allowlisted in
 * `script-src` instead — see the CSP block in `config.server.ts` and
 * `ai-docs/patterns/third-party-scripts.md`.
 *
 * @param src Agent URL, normally {@link DATADOG_RUM_AGENT_SRC}.
 */
export function buildDatadogLoaderSnippet(src: string): string {
    return `(function(h,o,u,n,d){h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)}};d=o.createElement(u);d.async=1;d.src=n;n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)})(window,document,'script','${src}','DD_RUM');`;
}

/** The subset of the Datadog RUM API this integration calls. */
export interface DatadogRumApi {
    /** Queued callbacks, present only while the stub is standing in for the real agent. */
    q?: unknown[];
    onReady?: (callback: () => void) => void;
    init?: (config: Record<string, unknown>) => void;
    /** Renames the view the agent already opened. Available since SDK 2.17.0. */
    setViewName?: (name: string) => void;
    addError?: (error: unknown, context?: Record<string, unknown>) => void;
    addAction?: (name: string, context?: Record<string, unknown>) => void;
    setGlobalContextProperty?: (key: string, value: unknown) => void;
}

declare global {
    interface Window {
        /** Datadog RUM API. Installed as a queue by the `<head>` stub, replaced by the real agent. */
        DD_RUM?: DatadogRumApi;
    }
}

/**
 * Whether initialisation has already been *requested* on this document.
 *
 * Note the distinction: this latches when {@link initDatadogRum} queues its callback, not when the
 * agent runs it. If the agent never arrives — CSP, ad blocker, network — the flag stays set and
 * `init()` never executes. That is deliberate rather than a gap to close: there is no retry path to
 * protect, and re-queueing on every remount would pile up callbacks that all fire at once if the
 * agent turns up late.
 *
 * Module scope rather than component state: React StrictMode remounts every effect in development
 * (`development.strictMode` is on), and `datadogRum.init()` warns and ignores a second call.
 */
let initRequested = false;

/** Reset module state. Test-only. */
export function resetDatadogRumForTests(): void {
    initRequested = false;
}

/** Whether the agent (or its queueing stub) is present on the page. */
export function isDatadogRumAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.DD_RUM?.onReady === 'function';
}

/**
 * Run a callback once the agent is ready, or drop it if the stub was never installed.
 *
 * Calls queue while the agent is still downloading. If the agent is blocked outright — CSP,
 * ad blocker, network — the queue simply never drains; entries are a handful of closures per
 * session, which is why this stays fire-and-forget rather than growing a timeout.
 */
function withRum(callback: (rum: DatadogRumApi) => void): void {
    if (typeof window === 'undefined') return;
    const stub = window.DD_RUM;
    if (typeof stub?.onReady !== 'function') return;

    stub.onReady(() => {
        // Re-read the global rather than closing over `stub`: the agent *replaces* window.DD_RUM
        // when it lands, so the value captured above is the queue, not the API.
        const rum = window.DD_RUM;
        if (rum) callback(rum);
    });
}

/**
 * Drop RUM error events that are really CSP violation reports from Cloudflare Page Shield.
 *
 * Not speculative: on the SFRA sites these accounted for ~11.7M events in 30 days and are not
 * application errors. Black Magic sits behind the same Cloudflare eCDN with the same Page Shield
 * feature available, so the filter ships here from day one rather than after the bill.
 *
 * Deliberately narrow — it matches only violations whose report went to the Page Shield endpoint,
 * so a future first-party or enforced CSP that reports elsewhere stays visible. Mirrors the
 * `beforeSend` in SFRA's `analytics/dataDogInit.js`.
 *
 * @returns `false` to drop the event, `true` to send it — Datadog's `beforeSend` contract.
 */
export function shouldSendRumEvent(event: { type?: string; error?: { source?: string; stack?: string } }): boolean {
    const isPageShieldReport =
        event.type === 'error' &&
        event.error?.source === 'report' &&
        typeof event.error.stack === 'string' &&
        event.error.stack.includes('csp-reporting.cloudflare.com');

    // The SDK collects `console.error()` as an error event by default, and React writes every error
    // a boundary catches to the console before our handler runs. Left alone that both double-counts
    // (once as `console`, once as the `custom` event `addDatadogError` sends) and smuggles back the
    // bot 404s `useReportErrorToDatadog` deliberately drops — inflating the error-rate monitor this
    // integration exists to make trustworthy. Reporting stays explicit: uncaught errors still arrive
    // as `source: 'source'`, and anything else worth seeing we send ourselves.
    const isConsoleEcho = event.type === 'error' && event.error?.source === 'console';

    return !isPageShieldReport && !isConsoleEcho;
}

/** Query parameters whose *values* must never reach Datadog. Compared lowercased. */
const REDACTED_QUERY_PARAMS = new Set(['token', 'email', 'code', 'otp', 'password', 'secret']);

/**
 * Strip credential-bearing query values out of a URL, preserving everything else.
 *
 * RUM records `view.url` and `view.referrer` verbatim, so any secret a route accepts in its query
 * string is persisted for the retention window. `/reset-password` takes a live SLAS token and the
 * shopper's email exactly that way. It is unreachable today — `accounts.enabled` is false, so the
 * route 302s before a document is ever served at that URL — but the config comment beside that flag
 * says "flip to true to re-enable", and on that day every reset session would ship a working
 * credential to a third party. Cheaper to redact now than to remember then.
 *
 * Returns the input unchanged when it will not parse, rather than dropping the event.
 */
export function redactUrl(url: string | undefined): string | undefined {
    if (!url) return url;

    try {
        const parsed = new URL(url);
        let changed = false;
        for (const key of [...parsed.searchParams.keys()]) {
            if (REDACTED_QUERY_PARAMS.has(key.toLowerCase())) {
                parsed.searchParams.set(key, 'REDACTED');
                changed = true;
            }
        }
        return changed ? parsed.toString() : url;
    } catch {
        return url;
    }
}

/** The subset of a RUM event `beforeSend` touches. */
interface RumEventLike {
    type?: string;
    error?: { source?: string; stack?: string };
    view?: { url?: string; referrer?: string };
}

/**
 * The agent's `beforeSend` hook: redact the event, then decide whether to send it.
 *
 * Redaction happens first and unconditionally, so it applies to events that go on to be dropped
 * too — a filter that runs first would be one refactor away from leaking.
 *
 * @returns `false` to drop the event, `true` to send it — Datadog's contract.
 */
export function beforeSendRumEvent(event: RumEventLike): boolean {
    if (event.view) {
        event.view.url = redactUrl(event.view.url);
        event.view.referrer = redactUrl(event.view.referrer);
    }

    return shouldSendRumEvent(event);
}

/**
 * Initialise Datadog RUM (UCR-3022). Idempotent, and a no-op before the stub has installed.
 *
 * **Consent model:** no gate. RUM loads on every page, matching the SFRA sites, where it is
 * enabled by the `datadogRumEnabled` site preference alone and is not wired to OneTrust. Its
 * `_dd_s` session cookie is disclosed through the same cookie table as SFRA's. If that
 * classification ever changes, this becomes a `applyConsent()` call in
 * `src/components/onetrust/apply-consent.ts` — the shape Adobe Launch already uses.
 *
 * Session Replay is off (`sessionReplaySampleRate: 0`), again matching SFRA. Turning it on is not
 * just this number: replay needs `worker-src blob:` in the CSP and a pass over
 * `defaultPrivacyLevel` and any field that must stay masked.
 */
export function initDatadogRum(config: DatadogRumConfig): void {
    if (initRequested || !config.applicationId || !config.clientToken) return;
    if (!isDatadogRumAvailable()) return;

    initRequested = true;

    withRum((rum) => {
        rum.init?.({
            applicationId: config.applicationId,
            clientToken: config.clientToken,
            site: config.site,
            service: config.service,
            env: config.env,
            // MRT's bundle id, so a regression can be attributed to the deploy that shipped it.
            // Empty on a laptop, where `BUNDLE_ID` is unset — see `isLocalDev()`.
            ...(config.version ? { version: config.version } : {}),
            sessionSampleRate: config.sessionSampleRate,
            sessionReplaySampleRate: 0,
            defaultPrivacyLevel: 'mask-user-input',
            trackResources: true,
            trackUserInteractions: true,
            trackLongTasks: true,
            beforeSend: beforeSendRumEvent,
        });
    });
}

/**
 * Rename the current RUM view.
 *
 * Without this the agent names views by raw URL, so `/product/12345` and `/product/12346` are two
 * distinct views and the Views list is unusable. `<DatadogViewTracker>` feeds it the route's own
 * `analytics.page.pageName` (`blackmagic:shop:hydroponics`), which the loaders already produce for
 * the Adobe data layer.
 *
 * **Renames, deliberately — it does not start a view.** `startView()` would open a *second* view on
 * top of the one the agent opened for the same navigation, ending the first: two views per page
 * load, and page-load metrics (LCP, CLS, INP all finalise after hydration) stranded on the
 * truncated one rather than on the named view the dashboards group by.
 *
 * The alternative fix is `trackViewsManually: true`, which stops the agent creating views at all.
 * Rejected: it delays the first view until React has hydrated, and it makes every view in the
 * product depend on this component not throwing. Renaming keeps the agent's lifecycle — a view
 * always exists, even for routes that expose no analytics payload and even if this code breaks.
 *
 * Datadog's `@datadog/browser-rum-react` router integration would do the naming automatically, but
 * only for apps that build their router with `createBrowserRouter`. This app is React Router in
 * framework mode — there is no `entry.client.tsx` and hydration runs through `HydratedRouter` — so
 * that integration has nothing to wrap.
 */
export function nameDatadogView(name: string): void {
    if (!name) return;
    withRum((rum) => {
        rum.setViewName?.(name);
    });
}

/**
 * Record the page type on every RUM event from here on.
 *
 * This is Datadog's *global* context, which persists until overwritten — so it must be set on every
 * navigation rather than only where a value is available, or a route with no analytics payload
 * inherits the previous page's type and mis-attributes everything recorded on it.
 */
export function setDatadogPageType(pageType: string): void {
    withRum((rum) => {
        rum.setGlobalContextProperty?.('pageType', pageType);
    });
}

/**
 * Report a caught error to RUM.
 *
 * For errors an error boundary has already swallowed — RUM sees uncaught errors on its own, but a
 * boundary that renders a fallback means nothing propagates to `window.onerror`, so those failures
 * are invisible unless reported explicitly.
 */
export function addDatadogError(error: unknown, context?: Record<string, unknown>): void {
    withRum((rum) => {
        rum.addError?.(error, context);
    });
}
